import React, { useState, useMemo, useCallback, useEffect, use } from 'react';
import { useSearchParams } from 'react-router-dom';
import { createPinHandler, createUnpinHandler } from '@/utils/optimistic/optimisticPin';
import { UploadingVideo } from '@/utils/optimistic/optimisticUpload';
import { QueuedVideo } from '@/utils/optimistic/optimisticGenerate';
import { type UserVideo, requestVideoDelete, fetchSharedVideo, fetchVideoById } from '@/api/videoApi';
import { type UserImage, requestImageDelete, deleteImage } from '@/api/imageApi';
import { useUploadImage } from '@/hooks/querry_hooks/dashboard/useImageMutations';
import { useUploadTranscript } from '@/hooks/querry_hooks/dashboard/useTranscriptionsQuery';
import { ErrorState, LoadingMoreIndicator } from '../ui/StateComponents';
import { DecorativeLine } from '../ui/BrandingComponents';
import LoadingVideos from '../loading/LoadingVIdeos';
import EmptyVideos from '../ui/EmptyVideos';
import UploadModal from './modals/UploadModal';
import StatusBadge from './ui/StatusBadge';
import { MobileVideoFilter, VideoFilters } from './gallery/VideoFilters';
import UserExtensionRequestsPanel from './cards/UserExtensionRequestsPanel';
// do import PinnedVideosSection from './gallery/PinnedVideosSection';
import UploadingTile from '@/utils/dashboard/UploadingTile';
import VideoDetailsModal from './gallery/VideoDetailsModal';
import { useInfiniteScroll } from '@/hooks/common/customHooks';
import { useScrollToTop } from '../../hooks/common/ScrollToTop';
import { ScrollToTopButton } from '../ui/ScrollToTopButton';
import TranscriptionCard from '../admin/cards/TranscriptionCard';
import { Transcription } from '@/hooks/dashboard/useTranscription';
import TranscriptionCompleteModal from './modals/TranscriptionCompleteModal';
import VirtualizedVideosSection from './gallery/VirtualizedVideosSection';
import VirtualizedImagesSection from './gallery/VirtualizedImagesSection';
import { SkeletonGrid } from '@/components/ui/skeleton';
import { ToastNotification } from '@/components/ui/common/ToastNotification';
import { createFavouriteSuccessCallback, createArchiveSuccessCallback } from '@/utils/common/toastCallbacks';

import { useVideoStatusCounts } from "@/hooks/querry_hooks/common/useVideoStatusCounts";
import { useWebSocket } from "@/utils/contexts/WebSocketContext";
import { deleteVideoAPI } from "@/api/videoApi";
import { getFilterDisplayConfig, hasActiveFilters } from '@/utils/common/filterUtils';
import { DeviceContext } from '@/hooks/common/DeviceContext';
import ToggleTab, { useToggleTabState } from './ui/ToggleTab';

interface VideoGalleryProps {
  isAdmin: boolean;
  userEmail: string | null;
  pinnedVideos: UserVideo[];
  userVideos?: UserVideo[];
  userImages?: UserImage[];
  queuedVideos?: QueuedVideo[];
  queuedImages?: QueuedVideo[];
  uploadingVideos?: UploadingVideo[];
  uploadingImages?: UploadingVideo[];
  onAttachImage?: (image: UserImage, tab: 'Videos' | 'Images') => void;
  onAttachVideoLastFrame?: (video: UserVideo) => Promise<void>;
  error?: string | null;
  imagesError?: string | null;
  onRetry?: () => void;
  onRetryImages?: () => void;
  isLoading?: boolean;
  isLoadingImages?: boolean;
  isLoadingMoreImages?: boolean;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  hasMoreImages?: boolean;
  totalUnfilteredCount?: number;
  totalUnfilteredCountImages?: number;
  pinVideoWebSocket: (videoId: string, user_Email?: string) => void;
  unpinVideoWebSocket: (videoId: string, user_Email?: string) => void;
  handleDownloadLastFrame?: (signedUrl: string) => Promise<void>;
  cancelQueuedVideo?: (queuedId: string) => void;
  handleCancel?: () => void;
  optimisticPinVideo?: (videoId: string, userEmail: string) => void;
  optimisticUnpinVideo?: (videoId: string) => void;
  loadMore: () => void;
  loadMoreImages?: () => void;
  predictivePrefetchVideos?: () => void;
  predictivePrefetchImages?: () => void;
  isAllowedToPin: boolean;
  fetchAllowedToPin: () => Promise<void>;
  serverFilters: {
    status?: string;
    sort_by?: string;
    video_type?: string;
  };
  setServerFilters: React.Dispatch<React.SetStateAction<{
    status?: string;
    sort_by?: string;
    video_type?: string;
  }>>;
  imageFilters: {
    status?: string;
    sort_by?: string;
  };
  setImageFilters: React.Dispatch<React.SetStateAction<{
    status?: string;
    sort_by?: string;
  }>>;
  transcriptions: Transcription[];
  isLoadingTranscriptions: boolean;
  isLoadingMoreTranscriptions?: boolean;
  pageValue: string;
  loadMoreTranscriptions: () => Promise<void>;
  hasMoreTranscriptions: boolean;
  totalUnfilteredCountTranscriptions?: number;
  deleteTranscription?: (transcriptionId: string) => Promise<void>;
  handleVideoUpload: (video: File, prompt: string, model: string) => Promise<void>;
  isUploading: boolean;
  uploadError: string | null;
  clearUploadError: () => void;
  clearUploadSuccess: () => void;
  onDeleteVideo?: (videoId: string) => Promise<void>;
  onDeleteImage?: (imageId: string) => Promise<void>;
  isGenerating: boolean;
  setIsGenerating: React.Dispatch<React.SetStateAction<boolean>>;
  addTranscription?: (transcription: Transcription) => void;
  localUploadingImages?: Array<{id: string, prompt: string, uploadStartTime: number, progress?: number}>;
  setLocalUploadingImages?: React.Dispatch<React.SetStateAction<Array<{id: string, prompt: string, uploadStartTime: number, progress?: number}>>>;
  onSharedVideoReady?: () => void;
}

type SortOption = 'recent' | 'oldest' | 'largest' | 'smallest';

// Helper functions to reduce cognitive complexity
const getInfiniteScrollHasMore = (pageValue: string, hasMore: boolean, isLoading: boolean, hasMoreImages: boolean, isLoadingImages: boolean, hasMoreTranscriptions: boolean, isLoadingTranscriptions: boolean) => {
  if (pageValue === "Videos") return hasMore && !isLoading;
  if (pageValue === "Images") return hasMoreImages && !isLoadingImages;
  return hasMoreTranscriptions && !isLoadingTranscriptions;
};

const getInfiniteScrollIsLoading = (pageValue: string, isLoadingMore: boolean, isLoading: boolean, isLoadingMoreImages: boolean, isLoadingMoreTranscriptions: boolean) => {
  if (pageValue === "Videos") return isLoadingMore || isLoading;
  if (pageValue === "Images") return isLoadingMoreImages;
  return isLoadingMoreTranscriptions;
};

const getInfiniteScrollOnLoadMore = (pageValue: string, loadMore: () => void, loadMoreImages: (() => void) | undefined, loadMoreTranscriptions: () => Promise<void>) => {
  if (pageValue === "Videos") return loadMore;
  if (pageValue === "Images") return loadMoreImages || (() => { });
  return loadMoreTranscriptions;
};

const getInfiniteScrollPredictiveFetch = (
  pageValue: string,
  predictivePrefetchVideos: (() => void) | undefined,
  predictivePrefetchImages: (() => void) | undefined
) => {
  if (pageValue === "Videos") return predictivePrefetchVideos;
  if (pageValue === "Images") return predictivePrefetchImages;
  return undefined; // No predictive fetch for transcriptions yet
};

// Helper functions for complex conditional logic
const shouldShowVideosGrid = (
  error: string | null,
  isLoading: boolean,
  pageValue: string,
  userVideos: any[],
  filteredVideos: any[],
  queuedVideos?: QueuedVideo[],
  uploadingVideos?: UploadingVideo[]
): boolean => {
  // Show grid if:
  // 1. No error and not loading
  // 2. On Video page
  // 3. AND (has videos OR has queued videos OR has uploading videos)
  const hasContent = (userVideos.length > 0 && filteredVideos.length > 0) ||
    (queuedVideos !== undefined && queuedVideos.length > 0) ||
    (uploadingVideos !== undefined && uploadingVideos.length > 0);

  return Boolean(!error && !isLoading && pageValue === "Videos" && hasContent);
};

const shouldShowEndOfContentIndicator = ({
  error,
  isLoading,
  pageValue,
  hasMore,
  userVideos,
  hasMoreImages,
  userImages,
  hasMoreTranscriptions,
  transcriptions,
}: {
  error: string | null;
  isLoading: boolean;
  pageValue: string;
  hasMore: boolean;
  userVideos: any[];
  hasMoreImages: boolean;
  userImages: any[];
  hasMoreTranscriptions: boolean;
  transcriptions: any[];
}): boolean => {
  return !error &&
    !isLoading &&
    (
      (pageValue === "Videos" && userVideos.length > 0 && !hasMore) ||
      (pageValue === "Images" && userImages.length > 0 && !hasMoreImages) ||
      (pageValue === "Transcripts" && transcriptions.length > 0 && !hasMoreTranscriptions)
    );
};

// Helper function to filter unique transcriptions
const getUniqueTranscriptions = (transcriptions: Transcription[]) => {
  return transcriptions.filter((transcription, index, self) =>
    index === self.findIndex(t =>
      t.transcription === transcription.transcription &&
      t.user_email === transcription.user_email
    )
  );
};

// Helper function to create video modal event handler
const createVideoModalHandler = (userVideos: UserVideo[], pinnedVideos: UserVideo[], setSelectedVideo: (video: UserVideo) => void) => {
  return async (event: CustomEvent) => {
    const { videoId } = event.detail;
    const video = userVideos.find(v => v.id === videoId) ||
      pinnedVideos.find(v => v.id === videoId);
    if (video) {
      setSelectedVideo(video);
    } else {
      // Video not in local list (e.g. original video of an extended clip) — fetch it
      const fetched = await fetchSharedVideo(videoId);
      if (fetched) {
        setSelectedVideo(fetched);
      }
    }
  };
};

// Helper function to create image modal event handler  
const createImageModalHandler = (userImages: UserImage[], setSelectedImage: (image: UserImage) => void) => {
  return (event: CustomEvent) => {
    const { imageId } = event.detail;
    const image = userImages.find(i => i.id === imageId);
    if (image) {
      setSelectedImage(image);
    }
  };
};

// Helper function to render video tab content
const renderVideoTabContent = ({
  error,
  isLoading,
  pageValue,
  userVideos,
  filteredVideos,
  queuedVideos,
  uploadingVideos,
  cancelQueuedVideo,
  handleCancel,
  userEmail,
  getStatusBadge,
  handleVideoClick,
  handlePinVideo,
  handleUnpinVideo,
  totalUnfilteredCount,
  serverFilters,
  isMobile,
  activeTab,
  pinnedVideos,
  isLoadingMore,
  onFavouriteSuccess,
  onArchiveSuccess,
  isAdmin,
  onAttachVideoLastFrame,
}: any) => {
  // Use the new filter display configuration for videos
  const hasActiveFiltersBool = hasActiveFilters(serverFilters);

  const filterConfig = getFilterDisplayConfig({
    totalCount: userVideos.length,
    totalUnfilteredCount: totalUnfilteredCount || 0,
    isLoading: isLoading || false,
    hasActiveFilters: hasActiveFiltersBool,
    contentType: 'videos'
  });

  if (isMobile && activeTab === 'Pinned' && pageValue === 'Videos' && pinnedVideos.length === 0) {
    return <EmptyVideos
      icon="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
      title="No pinned videos yet"
      description={filterConfig.message || "pin videos to see them here!"}
    />
  }

  if (pageValue !== 'Videos' || (isMobile && activeTab !== 'All')) {
    return null;
  };

  return (
    <>
      {/* Error State */}
      {error && (
        <ErrorState error={error} onRetry={() => { }} retryText="Retry" />
      )}

      {/* Skeleton Loading State */}
      {isLoading && !error && (
        <div className="px-4 lg:px-8">
          <SkeletonGrid type="video" count={12} />
        </div>
      )}

      {/* No Content State - when there's no content at all */}
      {filterConfig.shouldShowNoContentMessage && (
        <EmptyVideos
          icon="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
          title="No videos yet"
          description={filterConfig.message || "Start generating videos to see them here!"}
        />
      )}

      {/* No Filtered Results State - when content exists but filters return nothing */}
      {filterConfig.shouldShowNoFilteredResultsMessage && (
        <EmptyVideos
          icon="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          title="No videos found"
          description={filterConfig.message || "Try adjusting your filters to see more results"}
        />
      )}

      {/* Virtualized Videos Grid - handles 100k+ videos efficiently */}
      {shouldShowVideosGrid(error, isLoading, pageValue, userVideos, filteredVideos, queuedVideos, uploadingVideos) && (
        <div className="mb-8 px-4 lg:px-8">
          <VirtualizedVideosSection
            filteredVideos={filteredVideos}
            queuedVideos={queuedVideos}
            uploadingVideos={uploadingVideos}
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            cancelQueuedVideo={cancelQueuedVideo}
            handleCancel={handleCancel}
            userEmail={userEmail || ''}
            getStatusBadge={getStatusBadge}
            handleVideoClick={handleVideoClick}
            handlePinVideo={handlePinVideo}
            handleUnpinVideo={handleUnpinVideo}
            isAdmin={isAdmin}
            onFavouriteSuccess={(isFavourited: boolean) => onFavouriteSuccess(isFavourited)}
            onArchiveSuccess={(isArchived: boolean) => onArchiveSuccess(isArchived)}
            onAttachVideoLastFrame={onAttachVideoLastFrame}
          />
        </div>
      )}
    </>
  );
};

// Helper function to update filters based on page value
const updateFiltersForPage = (pageValue: string, key: string, value: string, setImageFilters: any, setServerFilters: any) => {
  if (pageValue === 'Images') {
    setImageFilters((prev: any) => ({
      ...prev,
      [key]: value
    }));
  } else {
    setServerFilters((prev: any) => ({
      ...prev,
      [key]: value
    }));
  }
};

// Helper function to create pin handler
const createPinVideoHandler = (optimisticPinVideo: any, pinVideoWebSocket: any, userEmail: string) => {
  if (!optimisticPinVideo) {
    return () => { };
  }
  return createPinHandler(optimisticPinVideo, pinVideoWebSocket, userEmail || '', 'user');
};

// Helper function to create unpin handler
const createUnpinVideoHandler = (optimisticUnpinVideo: any, unpinVideoWebSocket: any) => {
  return optimisticUnpinVideo
    ? createUnpinHandler(optimisticUnpinVideo, unpinVideoWebSocket, 'user')
    : () => { };
};

const VideoGallery: React.FC<VideoGalleryProps> = ({
  isAdmin,
  userEmail,
  onDeleteVideo: onDeleteVideoProp,
  onDeleteImage: onDeleteImageProp,
  pinnedVideos,
  userVideos = [],
  userImages = [],
  queuedVideos = [],
  queuedImages = [],
  uploadingVideos = [],
  uploadingImages = [],
  error = null,
  imagesError = null,
  onRetry,
  onRetryImages,
  isLoading = false,
  isLoadingImages = false,
  isLoadingMoreImages = false,
  isLoadingMore = false,
  hasMore = false,
  hasMoreImages = false,
  pinVideoWebSocket,
  unpinVideoWebSocket,
  handleDownloadLastFrame,
  cancelQueuedVideo,
  handleCancel,
  optimisticPinVideo,
  optimisticUnpinVideo,
  isAllowedToPin,
  fetchAllowedToPin,
  loadMore,
  loadMoreImages,
  predictivePrefetchVideos,
  predictivePrefetchImages,
  totalUnfilteredCount,
  totalUnfilteredCountImages,
  serverFilters,
  setServerFilters,
  imageFilters,
  setImageFilters,
  transcriptions,
  isLoadingTranscriptions,
  isLoadingMoreTranscriptions,
  pageValue,
  loadMoreTranscriptions,
  hasMoreTranscriptions,
  totalUnfilteredCountTranscriptions,
  deleteTranscription,
  handleVideoUpload,
  isUploading,
  uploadError,
  clearUploadError,
  clearUploadSuccess,
  isGenerating,
  setIsGenerating,
  addTranscription,
  localUploadingImages = [],
  setLocalUploadingImages,
  onAttachImage,
  onAttachVideoLastFrame,
  onSharedVideoReady,
}) => {
  const { isMobile } = use(DeviceContext) ?? { isMobile: false, isTablet: false };
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedVideo, setSelectedVideo] = useState<UserVideo | null>(null);
  const [selectedImage, setSelectedImage] = useState<UserImage | null>(null);
  const [showTranscriptionModal, setShowTranscriptionModal] = useState(false);
  const [selectedTranscription, setSelectedTranscription] = useState<Transcription | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [showTranscriptUploadModal, setShowTranscriptUploadModal] = useState(false);
  const [localUploadingTranscripts, setLocalUploadingTranscripts] = useState<Array<{id: string, fileName: string, uploadStartTime: number, progress?: number}>>([]);

  // Helper function to calculate transcript progress (30 seconds)
  const calculateTranscriptProgress = useCallback((transcript: {id: string, fileName: string, uploadStartTime: number, progress?: number}) => {
    if (!transcript.uploadStartTime) return transcript;
    const elapsedMs = Date.now() - transcript.uploadStartTime;
    const elapsedSec = elapsedMs / 1000;
    const maxDuration = 30; // 30 seconds
    const calculatedProgress = Math.min(95, (elapsedSec / maxDuration) * 100);
    return {
      ...transcript,
      progress: Math.round(calculatedProgress)
    };
  }, []);

  // Helper function to calculate image upload progress (1 minute)
  const calculateImageUploadProgress = useCallback((image: {id: string, prompt: string, uploadStartTime: number, progress?: number}) => {
    if (!image.uploadStartTime) return image;
    const elapsedMs = Date.now() - image.uploadStartTime;
    const elapsedMin = elapsedMs / (1000 * 60);
    const maxDuration = 1; // 1 minute
    const calculatedProgress = Math.min(95, (elapsedMin / maxDuration) * 100);
    return {
      ...image,
      progress: Math.round(calculatedProgress)
    };
  }, []);

  // Use image upload mutation hook
  const { mutateAsync: uploadImageMutation, isPending: isUploadingImage, error: imageUploadError } = useUploadImage();

  // Use transcript upload mutation hook
  const { mutateAsync: uploadTranscriptMutation, isPending: isUploadingTranscript, error: transcriptUploadError } = useUploadTranscript();

  // Progress update handler to reduce nesting
  const updateUploadProgress = useCallback(() => {
    // Calculate transcript upload progress
    setLocalUploadingTranscripts(prev => prev.map(item => calculateTranscriptProgress(item)));

    // Calculate image upload progress
    if (setLocalUploadingImages) {
      setLocalUploadingImages(prev => prev.map(item => calculateImageUploadProgress(item)));
    }
  }, [calculateTranscriptProgress, calculateImageUploadProgress, setLocalUploadingImages]);

  // Client-side progress simulation for uploading transcripts (30 seconds) and images (1 minute)
  useEffect(() => {
    const interval = setInterval(updateUploadProgress, 1000);
    return () => clearInterval(interval);
  }, [updateUploadProgress]);

  const [pinToastOpen, setPinToastOpen] = useState(false);
  const [pinToastMessage, setPinToastMessage] = useState('');
  
  const [favouriteToastOpen, setFavouriteToastOpen] = useState(false);
  const [favouriteToastMessage, setFavouriteToastMessage] = useState('');
  
  const [archiveToastOpen, setArchiveToastOpen] = useState(false);
  const [archiveToastMessage, setArchiveToastMessage] = useState('');

  const [gallerySubTab, setGallerySubTab] = useState<'Videos' | 'MyRequests'>('Videos');

  const { optimisticDeleteCounts } = useVideoStatusCounts({
    userEmail: userEmail || undefined,
  });
  const { showButton, scrollToTop } = useScrollToTop();
  // Listen for video modal events from notifications
  useEffect(() => {
    const handleOpenVideoModal = createVideoModalHandler(userVideos, pinnedVideos, setSelectedVideo);
    const handleOpenImageModal = createImageModalHandler(userImages, setSelectedImage);

    globalThis.addEventListener('openVideoModal', handleOpenVideoModal as unknown as EventListener);
    globalThis.addEventListener('openImageModal', handleOpenImageModal as EventListener);
    return () => {
      globalThis.removeEventListener('openVideoModal', handleOpenVideoModal as unknown as EventListener);
      globalThis.removeEventListener('openImageModal', handleOpenImageModal as EventListener);
    };
  }, [userVideos, pinnedVideos, userImages]);

  // Deep-link: auto-open video modal when ?video=<id> is in the URL.
  // sharedVideoLoadedRef prevents duplicate opens.
  // sharedVideoFetchingRef prevents duplicate in-flight API calls while the
  // gallery is still loading (effect re-runs on every userVideos change).
  const sharedVideoLoadedRef = React.useRef(false);
  const sharedVideoFetchingRef = React.useRef(false);
  const videoParamId = searchParams.get('video');

  useEffect(() => {
    if (!videoParamId) return;
    // Already showing this exact video — nothing to do.
    if (sharedVideoLoadedRef.current && selectedVideo?.id === videoParamId) return;
    // videoParamId changed — reset guards so the new video is loaded fresh.
    if (sharedVideoLoadedRef.current && selectedVideo?.id !== videoParamId) {
      sharedVideoLoadedRef.current = false;
      sharedVideoFetchingRef.current = false;
    }
    if (selectedVideo?.id === videoParamId) {
      sharedVideoLoadedRef.current = true;
      return;
    }

    // Fast path: video already in the loaded list (e.g. owner, or cached)
    const localVideo =
      userVideos.find(v => v.id === videoParamId) ??
      pinnedVideos.find(v => v.id === videoParamId);

    if (localVideo) {
      setSelectedVideo(localVideo);
      sharedVideoLoadedRef.current = true;
      onSharedVideoReady?.();
      return;
    }

    // Slow path: fetch from shared endpoint (non-owner, or video not yet in list).
    // Guard against duplicate in-flight requests while gallery pages are loading.
    if (sharedVideoFetchingRef.current) return;
    sharedVideoFetchingRef.current = true;

    fetchSharedVideo(videoParamId).then((video) => {
      sharedVideoFetchingRef.current = false;
      if (video && !sharedVideoLoadedRef.current) {
        setSelectedVideo(video);
        sharedVideoLoadedRef.current = true;
        onSharedVideoReady?.();
      }
    });
  }, [videoParamId, userVideos, pinnedVideos, onSharedVideoReady]);

  useInfiniteScroll({
    hasMore: getInfiniteScrollHasMore(pageValue, hasMore, isLoading, hasMoreImages, isLoadingImages, hasMoreTranscriptions, isLoadingTranscriptions),
    isLoading: getInfiniteScrollIsLoading(pageValue, isLoadingMore, isLoading, isLoadingMoreImages ?? false, isLoadingMoreTranscriptions ?? false),
    onLoadMore: getInfiniteScrollOnLoadMore(pageValue, loadMore, loadMoreImages, loadMoreTranscriptions),
    threshold: 200,
    predictiveThreshold: 600,
    onPredictiveFetch: getInfiniteScrollPredictiveFetch(pageValue, predictivePrefetchVideos, predictivePrefetchImages),
  })

  const filteredVideos = useMemo(() => {
    return userVideos;
  }, [userVideos]);

  const filteredPinnedVideos = useMemo(() => {
    return [...pinnedVideos];
  }, [pinnedVideos]);

  // Event handlers
  const handleVideoClick = useCallback((video: UserVideo) => {
    setSelectedVideo(video);
    setSearchParams({ video: video.id });
  }, [setSearchParams]);

  const handleImageClick = useCallback((image: UserImage) => {
    setSelectedImage(image);
  }, []);

  const openUploadModal = useCallback(() => {
    setShowUploadModal(true);
  }, []);

  const openImageUploadModal = useCallback(() => {
    setShowImageUploadModal(true);
  }, []);

  const openTranscriptUploadModal = useCallback(() => {
    setShowTranscriptUploadModal(true);
  }, []);

  const handleTranscriptionClick = useCallback((transcription: Transcription) => {
    setShowTranscriptionModal(true);
    setSelectedTranscription(transcription);
  }, []);

  const closeUploadModal = useCallback(() => {
    setShowUploadModal(false);
    clearUploadError();
    clearUploadSuccess();
  }, [clearUploadError, clearUploadSuccess]);

  const handleImageUpload = useCallback(async (image: File, prompt: string, model: string) => {
    // Create uploading tile immediately
    const uploadId = `upload_${Date.now()}_${crypto.randomUUID()}`;
    setLocalUploadingImages?.(prev => [...prev, { id: uploadId, prompt, uploadStartTime: Date.now() }]);
    
    try {
      await uploadImageMutation({ image, prompt, model });
      // Image upload success - remove uploading tile
      setLocalUploadingImages?.(prev => prev.filter(img => img.id !== uploadId));
    } catch (error) {
      console.error('Image upload failed:', error);
      // Remove uploading tile on error
      setLocalUploadingImages?.(prev => prev.filter(img => img.id !== uploadId));
      throw error; // Let modal handle the error
    }
  }, [uploadImageMutation, setLocalUploadingImages]);

  const handleTranscriptUpload = useCallback(async (file: File, _prompt: string, model: string) => {
    // Create uploading tile immediately
    const uploadId = `upload_${Date.now()}_${crypto.randomUUID()}`;
    setLocalUploadingTranscripts(prev => [...prev, { id: uploadId, fileName: file.name, uploadStartTime: Date.now() }]);
    
    try {
      const result = await uploadTranscriptMutation({ file, model });
      // Transcript upload success - remove uploading tile
      setLocalUploadingTranscripts(prev => prev.filter(t => t.id !== uploadId));
      
      // Add the new transcript to the list immediately
      if (addTranscription && result) {
        const newTranscription: Transcription = {
          title: result.title,
          transcription: result.transcription,
          user_email: result.user_email,
          created_at: result.created_at,
          id: result.id
        };
        addTranscription(newTranscription);
      }
    } catch (error) {
      console.error('Transcript upload failed:', error);
      // Remove uploading tile on error
      setLocalUploadingTranscripts(prev => prev.filter(t => t.id !== uploadId));
      throw error; // Let modal handle the error
    }
  }, [uploadTranscriptMutation, addTranscription]);

  const closeImageUploadModal = useCallback(() => {
    setShowImageUploadModal(false);
  }, []);

  const closeTranscriptUploadModal = useCallback(() => {
    setShowTranscriptUploadModal(false);
  }, []);

  const closeVideoModal = useCallback(() => {
    setSelectedVideo(null);
    setSearchParams({});
  }, [setSearchParams]);

  const closeImageModal = useCallback(() => {
    setSelectedImage(null);
  }, []);

  useEffect(() => {
    // Only fetch once on mount - don't run on every render
    fetchAllowedToPin()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handlePinVideo = useCallback((video: UserVideo) => {
    // Admins can always pin
    if (isAdmin) {
      const handler = createPinVideoHandler(optimisticPinVideo, pinVideoWebSocket, userEmail || '');
      handler(video);
      return;
    }

    // Check if user is allowed to pin (can only pin 1 video)
    if (!isAllowedToPin) {
      setPinToastMessage('You can only pin 1 video at a time. Please unpin your current video first.');
      setPinToastOpen(true);
      return;
    }

    // User is allowed to pin
    const handler = createPinVideoHandler(optimisticPinVideo, pinVideoWebSocket, userEmail || '');
    handler(video);
  }, [isAdmin, isAllowedToPin, optimisticPinVideo, pinVideoWebSocket, userEmail]);

  const handleUnpinVideo = useCallback((video: UserVideo) => {
    const handler = createUnpinVideoHandler(optimisticUnpinVideo, unpinVideoWebSocket);
    if (handler) {
      handler(video);
    }
  }, [optimisticUnpinVideo, unpinVideoWebSocket]);

  // Callback for favourite toggle success
  const handleFavouriteSuccess = useCallback(
    createFavouriteSuccessCallback(setFavouriteToastMessage, setFavouriteToastOpen),
    []
  );

  // Callback for archive toggle success
  const handleArchiveSuccess = useCallback(
    createArchiveSuccessCallback(setArchiveToastMessage, setArchiveToastOpen),
    []
  );

  const handleUploadComplete = useCallback(async (file: File, prompt: string, model: string) => {
    clearUploadError();
    clearUploadSuccess();

    if (!file || !prompt?.trim() || !model?.trim()) {
      return;
    }

    // Close modal immediately when upload starts
    closeUploadModal();

    try {
      await handleVideoUpload(file, prompt.trim(), model);
    } catch (error) {
      console.error('Video upload failed:', error);
      // Error state is handled by the useVideoGeneration hook
    }
  }, [handleVideoUpload, clearUploadError, clearUploadSuccess, closeUploadModal]);

  const handleRequestDelete = useCallback(async (videoId: string) => {

    try {
      await requestVideoDelete(videoId);

      // Update the selected video state to reflect the delete request
      if (selectedVideo?.id === videoId) {
        setSelectedVideo({
          ...selectedVideo,
          delete_request: true
        });
      }

      // Trigger a refetch to update the video list with delete_request status
      if (onRetry) {
        onRetry();
      }
    } catch (error) {
      console.error('[DEBUG] Failed to request video deletion. Error:', error);
      console.error('[DEBUG] Error details:', error instanceof Error ? error.message : error);

      // Check if the error is "Video not found"
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('Video not found')) {
        alert('This video no longer exists. The page will refresh to update the video list.');

        // Close the modal
        closeVideoModal();

        // Force refresh the video list
        if (onRetry) {
          onRetry();
        }
      } else {
        alert('Failed to request video deletion. Please try again.');
      }

      throw error; // Re-throw to let the modal handle the error
    }
  }, [onRetry, selectedVideo, closeVideoModal]);

  const handleRequestImageDelete = useCallback(async (imageId: string) => {
    try {
      await requestImageDelete(imageId);

      // Update the selected image state to reflect the delete request
      if (selectedImage?.id === imageId) {
        setSelectedImage({
          ...selectedImage,
          delete_request: true
        });
      }

      // Trigger a refetch to update the image list with delete_request status
      if (onRetryImages) {
        onRetryImages();
      }
    } catch (error) {
      console.error('Failed to request image deletion:', error);

      // Check if the error is "Image not found"
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('Image not found') || errorMessage.includes('not found')) {
        alert('This image no longer exists. The page will refresh to update the image list.');

        // Close the modal
        closeImageModal();

        // Force refresh the image list
        if (onRetryImages) {
          onRetryImages();
        }
      } else {
        alert('Failed to request image deletion. Please try again.');
      }

      throw error; // Re-throw to let the modal handle the error
    }
  }, [onRetryImages, selectedImage, closeImageModal]);
  // Reset filters when switching tabs
  const handleResetFilters = useCallback(() => {
    setServerFilters({
      status: 'all',
      sort_by: 'recent',
      video_type: 'all'
    });
    setImageFilters({
      status: 'all',
      sort_by: 'recent'
    });
  }, [setServerFilters, setImageFilters]);

  // Single filter change handler
  const handleFilterChange = useCallback((key: string, value: string) => {
    updateFiltersForPage(pageValue, key, value, setImageFilters, setServerFilters);
  }, [pageValue, setImageFilters, setServerFilters]);

  // Utility functions
  const getStatusBadge = useCallback((status?: string) => {
    return <StatusBadge status={status} />;
  }, []);

  const formatFileSize = useCallback((bytes: number) => {
    return `${Math.round(bytes / (1024 * 1024))} MB`;
  }, []);

  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  }, []);

  const { sendMessage } = useWebSocket();

  const handleDeleteVideo = useCallback(
    async (videoId: string) => {
      if (videoId != selectedVideo?.id) {
        return;
      }
      const currentVideo = selectedVideo;
      const videoStatus = currentVideo?.status || "pending";
      const videoDuration = currentVideo?.duration || 0;

      try {
        await deleteVideoAPI(videoId, userEmail);

        // On success, close modal and refetch data
        closeVideoModal();
        if (onRetry) {
          onRetry();
        }
        optimisticDeleteCounts(videoStatus, videoDuration);

        sendMessage({
          event: "video_deleted",
          video_id: videoId,
          data: {
            user_email: currentVideo?.user_email,
            status: videoStatus,
            duration: videoDuration,
            admin_email: userEmail,
            backend_confirmed: true,
            admin_optimistic: false,
          },
        });
      } catch (error) {
        sendMessage({
          event: "video_delete_error",
          video_id: videoId,
          data: {
            status: videoStatus,
            duration: videoDuration,
            admin_email: userEmail,
            error_revert: true,
          },
        });

        const errorMessage = `Failed to delete video: ${error instanceof Error ? error.message : "Unknown error"}`;

        if (globalThis.Notification && Notification.permission === "granted") {
          new Notification("Video Delete Failed", {
            body: errorMessage,
            icon: "/favicon.ico",
          });
        } else {
          alert(errorMessage);
        }
      }
    },
    [
      selectedVideo,
      optimisticDeleteCounts,
      sendMessage,
      userEmail,
      closeVideoModal,
      onRetry,
    ],
  );

  const handleDeleteImage = useCallback(
    async (imageId: string) => {
      if (imageId != selectedImage?.id) {
        return;
      }
      const currentImage = selectedImage;
      const imageStatus = currentImage?.status || "pending";

      try {
        await deleteImage(imageId);

        // On success, close modal and refetch data
        closeImageModal();
        if (onRetry) {
          onRetry();
        }
        // Note: We don't have image-specific optimistic counts yet
        // optimisticDeleteImageCounts(imageStatus);

        sendMessage({
          event: "image_deleted",
          image_id: imageId,
          data: {
            user_email: currentImage?.user_email,
            status: imageStatus,
            admin_email: userEmail,
            backend_confirmed: true,
            admin_optimistic: false,
          },
        });
      } catch (error) {
        sendMessage({
          event: "image_delete_error",
          image_id: imageId,
          data: {
            status: imageStatus,
            admin_email: userEmail,
            error_revert: true,
          },
        });

        const errorMessage = `Failed to delete image: ${error instanceof Error ? error.message : "Unknown error"}`;

        if (globalThis.Notification && Notification.permission === "granted") {
          new Notification("Image Delete Failed", {
            body: errorMessage,
            icon: "/favicon.ico",
          });
        } else {
          alert(errorMessage);
        }
      }
    },
    [
      selectedImage,
      sendMessage,
      userEmail,
      closeImageModal,
      onRetry,
    ],
  );

  const tabOptions = [
    { value: "Pinned", label: "Pinned" },
    { value: "All", label: "All" },
  ];

  const { activeTab, handleTabChange, redirectToAllTab } = useToggleTabState({
    options: tabOptions,
    defaultValue: "All",
    storageKey: "video-gallery-tab",
  });

  useEffect(() => {
    if (isGenerating) {
      redirectToAllTab();
      setIsGenerating(false);
    }
  }, [isGenerating, redirectToAllTab, setIsGenerating]);

  return (
    <div data-testid="video-gallery">
      <h2
        className="text-center text-gray-600 font-inter mb-0.5 mt-[60px] sm:mt-32 text-xl sm:text-2xl md:text-[20px]"
        style={{ marginBottom: "0.5rem" }}
      >
        Pickup where you left
      </h2>

      <DecorativeLine />

      {isMobile && pageValue === "Videos" &&
        (<div className='w-40 mx-auto'>
          <ToggleTab
            value={activeTab}
            onChange={handleTabChange}
            options={tabOptions}
            className="mb-4"
          />
        </div>)
      }

      {/* Commented out Pinned Videos Section */}
      {/* <PinnedVideosSection
        pinnedVideos={filteredPinnedVideos}
        getStatusBadge={getStatusBadge}
        onVideoClick={handleVideoClick}
        onPinVideo={handlePinVideo}
        onUnpinVideo={handleUnpinVideo}
        isMobile={isMobile}
        activeTab={activeTab}
        pageValue={pageValue}
        isAdmin={isAdmin}
        onFavouriteSuccess={(isFavourited: boolean) => handleFavouriteSuccess(isFavourited, 'video')}
      /> */}

      {/* Filter Controls (includes My Videos / My Requests tab switch) */}
      {(() => {
        const hasActiveFilters = pageValue === 'Images' ?
          (imageFilters.status !== 'all' || imageFilters.sort_by !== 'recent') :
          (serverFilters.status !== 'all' || serverFilters.sort_by !== 'recent' || serverFilters.video_type !== 'all');

        // Determine content type based on current page
        let contentType: 'videos' | 'images' | 'transcriptions' = 'videos';
        if (pageValue === 'Images') {
          contentType = 'images';
        } else if (pageValue === 'Transcripts') {
          contentType = 'transcriptions';
        }

        const filterConfig = getFilterDisplayConfig({
          totalCount: userVideos.length,
          totalUnfilteredCount: totalUnfilteredCount || 0,
          isLoading: isLoading || false,
          hasActiveFilters,
          contentType
        });

        const showFilters = filterConfig.shouldShowFilters;

        const sortByValue =
          pageValue === 'Images'
            ? imageFilters.sort_by || 'recent'
            : serverFilters.sort_by || 'recent';

        const statusValue =
          pageValue === 'Images'
            ? imageFilters.status || 'all'
            : serverFilters.status || 'all';

        const videoTypeValue = serverFilters.video_type || 'all';

        const tabSlot = (pageValue === 'Videos') ? (
          <ToggleTab
            value={gallerySubTab}
            onChange={(v) => setGallerySubTab(v as 'Videos' | 'MyRequests')}
            options={[
              { value: 'Videos', label: 'My Videos' },
              { value: 'MyRequests', label: 'My Requests' },
            ]}
          />
        ) : undefined;

        if (!showFilters && !tabSlot) return null;

        if (isMobile && pageValue === "Videos" && activeTab === "Pinned") return null;

        const FiltersComponent = isMobile ? MobileVideoFilter : VideoFilters;

        return (
          <FiltersComponent
            pageValue={pageValue}
            sortBy={sortByValue}
            selectedStatus={statusValue}
            selectedVideoType={videoTypeValue}
            onSortChange={(sort) => handleFilterChange('sort_by', sort as SortOption)}
            onStatusChange={(status) => handleFilterChange('status', status)}
            onVideoTypeChange={(videoType) => handleFilterChange('video_type', videoType)}
            showUploadButton={true}
            onUploadClick={openUploadModal}
            onImageUploadClick={openImageUploadModal}
            onTranscriptUploadClick={openTranscriptUploadModal}
            hasVideos={(totalUnfilteredCount || 0) > 0}
            onResetFilters={handleResetFilters}
            tabSlot={tabSlot}
            hideControls={gallerySubTab === 'MyRequests'}
          />
        );
      })()}

      {/* My Extension Requests panel */}
      {pageValue === 'Videos' && gallerySubTab === 'MyRequests' && (
        <UserExtensionRequestsPanel
          onViewExtended={async (videoId: string) => {
            try {
              const video = await fetchVideoById(videoId, false)
              if (video) setSelectedVideo(video)
            } catch {
              // ignore
            }
          }}
        />
      )}

      {/* Video Tab Content */}
      {gallerySubTab !== 'MyRequests' && renderVideoTabContent({
        error,
        isLoading,
        pageValue,
        userVideos,
        filteredVideos,
        queuedVideos,
        uploadingVideos,
        cancelQueuedVideo,
        handleCancel,
        userEmail,
        getStatusBadge,
        handleVideoClick,
        handlePinVideo,
        handleUnpinVideo,
        totalUnfilteredCount,
        serverFilters,
        isMobile,
        activeTab,
        pinnedVideos: filteredPinnedVideos,
        isLoadingMore,
        hasMore,
        isAdmin,
        onFavouriteSuccess: (isFavourited: boolean) => handleFavouriteSuccess(isFavourited, 'video'),
        onArchiveSuccess: (isArchived: boolean) => handleArchiveSuccess(isArchived, 'video'),
        onAttachVideoLastFrame,
      })}

      {/* Images Section */}
      {pageValue === "Images" && (() => {
        // Check if there are active image filters
        const hasActiveImageFilters = hasActiveFilters(imageFilters);

        const imageFilterConfig = getFilterDisplayConfig({
          totalCount: userImages.length,
          totalUnfilteredCount: totalUnfilteredCountImages || 0,
          isLoading: isLoadingImages || false,
          hasActiveFilters: hasActiveImageFilters,
          contentType: 'images'
        });

        return (
          <div className="mb-8 px-4 lg:px-8">
            {/* Error State */}
            {imagesError && (
              <ErrorState error={imagesError} onRetry={onRetryImages} retryText="Retry" />
            )}

            {/* Skeleton Loading State */}
            {isLoadingImages && !imagesError && userImages.length === 0 && (
              <SkeletonGrid type="image" count={12} />
            )}

            {/* No Content State - when there's no content at all */}
            {imageFilterConfig.shouldShowNoContentMessage && (
              <EmptyVideos
                icon="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                title="No images yet"
                description={imageFilterConfig.message || "Start generating images to see them here!"}
              />
            )}

            {/* No Filtered Results State - when content exists but filters return nothing */}
            {imageFilterConfig.shouldShowNoFilteredResultsMessage && (
              <EmptyVideos
                icon="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                title="No images found"
                description={imageFilterConfig.message || "Try adjusting your filters to see more results"}
              />
            )}

            {/* Virtualized Images Content - handles 100k+ images efficiently */}
            {!imagesError && (userImages.length > 0 || queuedImages.length > 0 || uploadingImages.length > 0 || localUploadingImages.length > 0) && (
              <VirtualizedImagesSection
                filteredImages={userImages}
                queuedImages={queuedImages}
                uploadingImages={localUploadingImages.map(img => ({
                  id: img.id,
                  prompt: img.prompt,
                  model: 'Flow',
                  status: 'uploading' as const,
                  progress: img.progress,
                  uploadStartTime: img.uploadStartTime,
                  file: null,
                  createdAt: new Date().toISOString()
                }))}
                isLoadingImages={isLoadingImages || false}
                isLoadingMoreImages={isLoadingMoreImages}
                imagesError={imagesError}
                onRetryImages={onRetryImages}
                handleCancel={handleCancel}
                cancelQueuedVideo={cancelQueuedVideo}
                handleImageClick={handleImageClick}
                onAttachImage={onAttachImage}
                isAdmin={isAdmin}
                onFavouriteSuccess={(isFavourited: boolean) => handleFavouriteSuccess(isFavourited, 'image')}
                onArchiveSuccess={(isArchived: boolean) => handleArchiveSuccess(isArchived, 'image')}
              />
            )}
          </div>
        );
      })()}

      {/* Transcripts Section */}
      {pageValue === "Transcripts" && (() => {
        // Transcription filters are simpler - just showing all for now
        const hasActiveTranscriptFilters = false; // No filters implemented yet for transcriptions

        const transcriptFilterConfig = getFilterDisplayConfig({
          totalCount: transcriptions.length,
          totalUnfilteredCount: totalUnfilteredCountTranscriptions || 0,
          isLoading: isLoadingTranscriptions || false,
          hasActiveFilters: hasActiveTranscriptFilters,
          contentType: 'transcriptions'
        });

        return (
          <>
            {/* Error State */}
            {error && (
              <ErrorState error={error} onRetry={() => { }} retryText="Retry" />
            )}

            {/* Loading State */}
            {isLoadingTranscriptions && !error && transcriptions.length === 0 && localUploadingTranscripts.length === 0 && (
              <LoadingVideos />
            )}

            {/* No Content State - when there's no content at all */}
            {transcriptFilterConfig.shouldShowNoContentMessage && (
              <EmptyVideos
                icon="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                title="No transcriptions yet"
                description={transcriptFilterConfig.message || "Start creating transcriptions to see them here!"}
              />
            )}

            {/* No Filtered Results State - when content exists but filters return nothing */}
            {transcriptFilterConfig.shouldShowNoFilteredResultsMessage && (
              <EmptyVideos
                icon="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                title="No transcriptions found"
                description={transcriptFilterConfig.message || "Try adjusting your filters to see more results"}
              />
            )}

            {/* Transcriptions Grid */}
            {!error && !isLoadingTranscriptions && (transcriptions.length > 0 || localUploadingTranscripts.length > 0) && (
              <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8 px-4 lg:px-8">
                {/* Uploading Transcripts */}
                {localUploadingTranscripts.map((uploadingTranscript) => (
                  <UploadingTile
                    key={uploadingTranscript.id}
                    status="uploading"
                    progress={uploadingTranscript.progress}
                    aspectRatio="16/9"
                  />
                ))}
                
                {/* Existing Transcriptions */}
                {getUniqueTranscriptions(transcriptions)
                  .map((transcription: Transcription, index: number) => (
                    <TranscriptionCard
                      key={`${transcription.created_at}-${transcription.user_email}-${index}-${transcription.title}`}
                      title={transcription.title || "Untitled"}
                      wordCount={transcription.transcription.split(' ').length}
                      date={new Date(transcription.created_at).toLocaleDateString()}
                      onSelect={() => handleTranscriptionClick(transcription)}
                    />
                  ))}
              </div>
              
              {/* Loading More Indicator for Transcriptions */}
              {isLoadingMoreTranscriptions && (
                <LoadingMoreIndicator isVisible={true} message="Loading more transcriptions..." />
              )}
              </>
            )}
          </>
        );
      })()}

      {/* End of Content Indicator */}
      {gallerySubTab !== 'MyRequests' && shouldShowEndOfContentIndicator({
        error, isLoading, pageValue, hasMore, userVideos,
        hasMoreImages, userImages, hasMoreTranscriptions, transcriptions
      }) && (
          <div className="flex justify-center items-center py-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-3">
                <svg
                  className="h-6 w-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <p className="text-gray-600 font-medium text-sm">
                You've reached the end!
              </p>
              <p className="text-gray-500 text-xs mt-1">
                {pageValue === "Videos" && "All videos have been loaded"}
                {pageValue === "Images" && "All images have been loaded"}
                {pageValue === "Transcripts" && "All transcriptions have been loaded"}
              </p>
            </div>
          </div>
        )}

      {/* Transcription Modal */}
      <TranscriptionCompleteModal
        isOpen={showTranscriptionModal}
        onClose={() => setShowTranscriptionModal(false)}
        onDelete={deleteTranscription && selectedTranscription?.id ? async () => {
          const transcriptionId = selectedTranscription.id;
          if (!transcriptionId) return;
          try {
            await deleteTranscription(transcriptionId);
            setShowTranscriptionModal(false);
            setSelectedTranscription(null);
          } catch (error) {
            console.error('Failed to delete transcription:', error);
          }
        } : undefined}
        onDownload={() => {
          if (selectedTranscription) {
            const blob = new Blob([selectedTranscription.transcription], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${selectedTranscription.title || 'transcription'}.txt`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
          }
        }}
        transcriptionTitle={selectedTranscription?.title || ''}
        transcriptionText={selectedTranscription?.transcription || ''}
      />
      {/* Upload Modal */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={closeUploadModal}
        uploadType={pageValue === 'Images' ? 'image' : 'video'}
        uploadingVideos={uploadingVideos}
        onUpload={handleUploadComplete}
        isUploading={isUploading}
        uploadError={uploadError}
      />

      {/* Image Upload Modal */}
      <UploadModal
        isOpen={showImageUploadModal}
        onClose={closeImageUploadModal}
        uploadType="image"
        onUpload={handleImageUpload}
        isUploading={isUploadingImage}
        uploadError={imageUploadError?.message || null}
      />

      {/* Transcript Upload Modal */}
      <UploadModal
        isOpen={showTranscriptUploadModal}
        onClose={closeTranscriptUploadModal}
        uploadType="transcript"
        onUpload={handleTranscriptUpload}
        isUploading={isUploadingTranscript}
        uploadError={transcriptUploadError?.message || null}
      />

      {/* Content Details Modal */}
      <VideoDetailsModal
        selectedVideo={selectedVideo}
        selectedImage={selectedImage}
        onClose={selectedVideo ? closeVideoModal : closeImageModal}
        formatDate={formatDate}
        formatFileSize={formatFileSize}
        onDownloadLastFrame={handleDownloadLastFrame}
        onRequestDelete={handleRequestDelete}
        onRequestImageDelete={handleRequestImageDelete}
        isAdmin={isAdmin}
        pageValue={pageValue}
        onDelete={onDeleteVideoProp || (isAdmin ? handleDeleteVideo : undefined)}
        onImageDelete={onDeleteImageProp || (isAdmin ? handleDeleteImage : undefined)}
      />
      {showButton && (
        <ScrollToTopButton scrollToTop={scrollToTop} />
      )}

      {/* Pin Limit Toast Notification */}
      <ToastNotification
        open={pinToastOpen}
        message={pinToastMessage}
        severity="warning"
        autoHideDuration={4000}
        onClose={() => setPinToastOpen(false)}
      />

      {/* Favourite Toast Notification */}
      <ToastNotification
        open={favouriteToastOpen}
        message={favouriteToastMessage}
        onClose={() => setFavouriteToastOpen(false)}
      />

      {/* Archive Toast Notification */}
      <ToastNotification
        open={archiveToastOpen}
        message={archiveToastMessage}
        onClose={() => setArchiveToastOpen(false)}
      />
    </div>
  );
};

export default React.memo(VideoGallery);
